import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import CharacterLobby from './CharacterLobby.tsx';
import CharacterDetail from './CharacterDetail.tsx';
import ImageGenerate from './ImageGenerate.tsx';
import LoginModal from './LoginModal.tsx';
import PricingModal from './PricingModal.tsx';

// Admin Panel Components
import AdminPanel from './admin/AdminPanel.tsx';
import AdminDashboard from './admin/AdminDashboard.tsx';
import AdminUsers from './admin/AdminUsers.tsx';
import AdminCharacters from './admin/AdminCharacters.tsx';
import AdminComments from './admin/AdminComments.tsx';
import AdminSuggestions from './admin/AdminSuggestions.tsx';
import AdminSystem from './admin/AdminSystem.tsx';

// Importar el componente App original para el chat
import App from '../App.tsx';
import { API_URL } from '../config/api.ts';

interface Character {
  _id: string;
  name: string;
  description: string;
  personality: string;
  systemPrompt: string;
  avatar: string;
  backgroundImage: string;
  tags: string[];
  isDefault: boolean;
  createdBy: string;
}

// Componente wrapper para manejar la navegación desde el lobby
const LobbyWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  const handleSelectCharacter = (character: Character) => {
    // Navegar directamente al chat con el personaje
    navigate(`/chat?character=${character._id}`);
  };

  const handleViewCharacterDetail = (character: Character) => {
    // Navegar a la página de detalles
    navigate(`/character/${character._id}`);
  };

  const handleLogin = () => {
    setShowLoginModal(true);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <CharacterLobby
        onSelectCharacter={handleSelectCharacter}
        onViewCharacterDetail={handleViewCharacterDetail}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onOpenPricing={() => setShowPricingModal(true)}
      />
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => {
          setShowLoginModal(false);
          window.location.reload();
        }}
      />
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />
    </>
  );
};

// Componente wrapper para manejar la navegación desde el detalle
const CharacterDetailWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [character, setCharacter] = useState<Character | null>(null);
  const [loading, setLoading] = useState(true);

  // Obtener el ID del personaje desde la URL
  const characterId = window.location.pathname.split('/character/')[1];

  useEffect(() => {
    const fetchCharacter = async () => {
      try {
        const response = await fetch(`${API_URL}/characters/${characterId}`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.success) {
          setCharacter(data.character || null);
        }
      } catch (error) {
        console.error('Error loading character:', error);
      } finally {
        setLoading(false);
      }
    };

    if (characterId) {
      fetchCharacter();
    } else {
      setLoading(false);
    }
  }, [characterId]);

  const handleBack = () => {
    navigate('/');
  };

  const handleStartChat = (character: Character, chatId?: string) => {
    let url = `/chat?character=${character._id}`;
    if (chatId) {
      url += `&chat=${chatId}`;
    }
    navigate(url);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)',
        color: 'white'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '18px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '3px solid rgba(255,107,107,0.3)',
            borderTop: '3px solid #ff6b6b',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading character...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!character) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)',
        color: 'white',
        textAlign: 'center',
        gap: '20px'
      }}>
        <h1 style={{ fontSize: '48px', marginBottom: '16px' }}>🤔</h1>
        <h2 style={{ fontSize: '24px', marginBottom: '8px' }}>Personaje no encontrado</h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '24px' }}>
          El personaje que buscas no existe o ha sido eliminado.
        </p>
        <button
          onClick={handleBack}
          style={{
            background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
            color: 'white',
            border: 'none',
            borderRadius: '16px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Volver a la Galería
        </button>
      </div>
    );
  }

  return (
    <CharacterDetail
      character={character}
      onBack={handleBack}
      onStartChat={handleStartChat}
    />
  );
};

// Componente wrapper para el chat - requires authentication
const ChatWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  // Obtener el parámetro del personaje desde la URL
  const urlParams = new URLSearchParams(window.location.search);
  const characterId = urlParams.get('character');
  const chatId = urlParams.get('chat');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/user`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.success && data.user) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)',
        color: 'white'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '18px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '3px solid rgba(255,107,107,0.3)',
            borderTop: '3px solid #ff6b6b',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading chat...
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to lobby
  if (!isAuthenticated) {
    // Redirect to character detail page if we have a characterId, otherwise to lobby
    if (characterId) {
      navigate(`/character/${characterId}`, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
    return null;
  }

  return <App initialCharacterId={characterId} initialChatId={chatId} isAlreadyLoggedIn={true} />;
};

// Componente wrapper para Image Generation - requires admin role
const GenerateWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<'loading' | 'unauthorized' | 'not-admin' | 'authorized'>('loading');
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/user`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.success && data.user) {
          // Check if user is admin
          if (data.user.role === 'admin') {
            setAuthState('authorized');
            setUserData(data.user);
          } else {
            setAuthState('not-admin');
          }
        } else {
          setAuthState('unauthorized');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setAuthState('unauthorized');
      }
    };

    checkAuth();
  }, []);

  // Loading state
  if (authState === 'loading') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)',
        color: 'white'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '18px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '3px solid rgba(167,139,250,0.3)',
            borderTop: '3px solid #a78bfa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading...
        </div>
      </div>
    );
  }

  // Not authenticated or not admin - redirect to lobby
  if (authState === 'unauthorized' || authState === 'not-admin') {
    navigate('/', { replace: true });
    return null;
  }

  return <ImageGenerate isDarkMode={true} userData={userData} />;
};

// Componente wrapper para manejar autenticación
const AuthWrapper: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/chats`, {
        credentials: 'include'
      });
      await response.json();
      // Auth check complete, just need to stop loading
    } catch (error) {
      console.error('Error checking authentication:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)',
        color: 'white'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '18px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '3px solid rgba(255,107,107,0.3)',
            borderTop: '3px solid #ff6b6b',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Verifying authentication...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // if (!isLoggedIn) block removed to allow access to public routes


  return (
    <Routes>
      {/* Ruta principal - Galería de personajes */}
      <Route path="/" element={<LobbyWrapper />} />

      {/* Ruta de detalle del personaje */}
      <Route path="/character/:id" element={<CharacterDetailWrapper />} />

      {/* Ruta del chat */}
      <Route path="/chat" element={<ChatWrapper />} />

      {/* Image Generation Route */}
      <Route path="/generate" element={<GenerateWrapper />} />

      {/* Admin Panel Routes */}
      <Route path="/admin" element={<AdminPanel />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="characters" element={<AdminCharacters />} />
        <Route path="comments" element={<AdminComments />} />
        <Route path="suggestions" element={<AdminSuggestions />} />
        <Route path="system" element={<AdminSystem />} />
      </Route>

      {/* Redireccionar cualquier ruta no encontrada al lobby */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Componente principal del router
const AppRouter: React.FC = () => {
  return (
    <Router>
      <AuthWrapper />
    </Router>
  );
};

export default AppRouter;


